import {
  BadgeDollarSign,
  Bell,
  Bookmark,
  BriefcaseBusiness,
  Building2,
  Compass,
  HelpCircle,
  Home,
  LogIn,
  Menu,
  MessageCircle,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  X,
} from 'lucide-react';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';

const studentLinks = [
  { to: '/', label: 'Home', icon: Home, end: true },
  { to: '/explore', label: 'Explore', icon: Compass },
  { to: '/requests/new', label: 'Find for me', icon: Sparkles, protected: true },
  { to: '/saved', label: 'Saved', icon: Bookmark, protected: true },
  { to: '/messages', label: 'Messages', icon: MessageCircle, protected: true },
];

export function PublicShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerQuery, setHeaderQuery] = useState('');
  const headerSearchRef = useRef<HTMLInputElement>(null);
  const initials = user?.name.split(/\s+/).slice(0, 2).map(word => word[0]).join('').toUpperCase() || 'HG';
  const destination = (to: string, needsAuth?: boolean) => needsAuth && !user ? `/login?redirectTo=${encodeURIComponent(to)}` : to;

  useEffect(() => {
    document.body.classList.toggle('nav-open', menuOpen);
    return () => document.body.classList.remove('nav-open');
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!menuOpen) return;

    function closeMenu(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false);
    }

    document.addEventListener('keydown', closeMenu);
    return () => document.removeEventListener('keydown', closeMenu);
  }, [menuOpen]);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1181px)');
    const closeMenuOnDesktop = (event: MediaQueryListEvent) => {
      if (event.matches) setMenuOpen(false);
    };

    desktop.addEventListener('change', closeMenuOnDesktop);
    return () => desktop.removeEventListener('change', closeMenuOnDesktop);
  }, []);

  useEffect(() => {
    if (location.pathname === '/explore') setHeaderQuery(new URLSearchParams(location.search).get('q') || '');
  }, [location.pathname, location.search]);

  useEffect(() => {
    function focusHeaderSearch(event: KeyboardEvent) {
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target as HTMLElement;
      if (target.matches('input, textarea, select, [contenteditable="true"]')) return;
      event.preventDefault();
      headerSearchRef.current?.focus();
    }
    document.addEventListener('keydown', focusHeaderSearch);
    return () => document.removeEventListener('keydown', focusHeaderSearch);
  }, []);

  function submitHeaderSearch(event: FormEvent) {
    event.preventDefault();
    const query = headerQuery.trim();
    navigate(query ? `/explore?q=${encodeURIComponent(query)}` : '/explore');
  }

  return (
    <div className="public-shell product-shell">
      <header className="public-header product-header">
        <div className="public-header-inner">
          <Link to="/" className="public-brand" aria-label="HostelGrid home"><Logo /></Link>

          <nav className="public-nav" aria-label="Marketplace navigation">
            {studentLinks.map(({ to, label, protected: needsAuth, end }) => (
              <NavLink key={to} to={destination(to, needsAuth)} end={end} className={({ isActive }) => `public-nav-link ${isActive ? 'active' : ''}`}>
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="public-actions">
            <form className="header-search-form" role="search" onSubmit={submitHeaderSearch}>
              <input ref={headerSearchRef} value={headerQuery} onChange={event => setHeaderQuery(event.target.value)} aria-label="Search hostels" placeholder="Search hostels" />
              <kbd>/</kbd>
              <button type="submit" aria-label="Submit hostel search"><Search size={16}/></button>
            </form>
            <Link className="header-advertise" to="/advertise"><BadgeDollarSign size={16}/><span>Advertise</span></Link>
            <Link className="header-represent" to="/representative/access"><Building2 size={16}/><span>Claim or add hostel</span></Link>
            {user?.agencyId
              ? <Link className="role-jump" to="/agency"><BriefcaseBusiness size={15}/><span>Agency workspace</span></Link>
              : <Link className="header-agency" to={destination('/agency/onboarding', true)}><BriefcaseBusiness size={16}/><span>Join as an agency</span></Link>}
            {user?.role === 'admin' && <Link className="role-jump admin" to="/admin"><ShieldCheck size={15}/><span>Admin</span></Link>}
            {user && <button className="icon-button header-notification" aria-label="Notifications" onClick={() => navigate('/notifications')}><Bell size={18}/></button>}
            <ThemeToggle />
            <button className="account-chip icon-only" onClick={() => navigate(user ? '/settings' : '/login')} aria-label={user ? `Account settings for ${user.name}` : 'Sign in'} title={user ? 'Account settings' : 'Sign in'}>
              <span>{initials}</span>
            </button>
            <button className="mobile-menu-button" aria-expanded={menuOpen} aria-controls="mobile-navigation-menu" aria-label={menuOpen ? 'Close menu' : 'Open menu'} onClick={() => setMenuOpen(value => !value)}>
              {menuOpen ? <X size={21}/> : <Menu size={21}/>} 
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div id="mobile-navigation-menu" className="mobile-menu-panel" role="dialog" aria-modal="true" aria-label="Navigation menu">
          <div className="mobile-menu-primary">
            {studentLinks.map(({ to, label, icon: Icon, protected: needsAuth, end }) => (
              <NavLink key={to} to={destination(to, needsAuth)} end={end} onClick={() => setMenuOpen(false)}>
                <Icon size={19}/><span>{label}</span>
              </NavLink>
            ))}
          </div>
          <div className="mobile-menu-secondary">
            <Link to="/advertise" onClick={() => setMenuOpen(false)}><BadgeDollarSign size={18}/>Advertise</Link>
            <Link to="/representative/access" onClick={() => setMenuOpen(false)}><Building2 size={18}/>Claim or add hostel</Link>
            <Link to={user?.agencyId ? '/agency' : destination('/agency/onboarding', true)} onClick={() => setMenuOpen(false)}><BriefcaseBusiness size={18}/>{user?.agencyId ? 'Agency workspace' : 'Join as an agency'}</Link>
            <Link to="/help" onClick={() => setMenuOpen(false)}><HelpCircle size={18}/>Help & safety</Link>
            <Link to={user ? '/settings' : '/login'} onClick={() => setMenuOpen(false)}>{user ? <Settings size={18}/> : <LogIn size={18}/>}{user ? 'Account settings' : 'Sign in'}</Link>
          </div>
        </div>
      )}

      <main className="public-main-content public-content"><Outlet /></main>

      <footer className="public-footer"><div><Logo/><p>Independent hostel discovery for students in Ghana.</p></div><nav aria-label="Footer navigation"><Link to={user?.agencyId ? '/agency' : destination('/agency/onboarding', true)}>{user?.agencyId ? 'Agency workspace' : 'Join as an agency'}</Link><Link to="/help">Help & safety</Link><Link to="/contact">Contact</Link><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link></nav><small>© {new Date().getFullYear()} HostelGrid</small></footer>

      <nav className="student-mobile-nav" aria-label="Mobile navigation">
        <NavLink to="/" end className={({ isActive }) => `student-mobile-link ${isActive ? 'active' : ''}`}><Home size={19}/><span>Home</span></NavLink>
        <NavLink to="/explore" className={({ isActive }) => `student-mobile-link ${isActive ? 'active' : ''}`}><Search size={19}/><span>Explore</span></NavLink>
        <NavLink to={destination('/saved', true)} className={({ isActive }) => `student-mobile-link ${isActive ? 'active' : ''}`}><Bookmark size={19}/><span>Saved</span></NavLink>
        <NavLink to={destination('/messages', true)} className={({ isActive }) => `student-mobile-link ${isActive ? 'active' : ''}`}><MessageCircle size={19}/><span>Messages</span></NavLink>
      </nav>
    </div>
  );
}
